import React, { useEffect, useRef, useState } from "react";
import intl from "react-intl-universal";
import Push from "push.js";
import { mdiPlus, mdiRefresh, mdiUpload } from "@mdi/js";

import commonUtils from "../../script/utils/commonUtils";

import fieldRest from "../../rest/fieldRest";
import antennaRest from "../../rest/antennaRest";

import { Table } from "../components/Table";
import { Button } from "../components/Button";
import { Spacer } from "../components/Spacer";
import { Search } from "../components/Search";
import { Layout } from "../containers/Layout";
import { useDebounce } from "../hooks/useDebounce";
import { LazyScroll } from "../components/LazyScroll";
import { ContentBody } from "../containers/ContentBody";
import { useSse } from "../hooks/useSse";
import { ContentHeader } from "../containers/ContentHeader";
import { ValidationProvider } from "../contexts/ValidationContext";
import { WorstcaseDataItem } from "./worstcaseData/WorstcaseDataItem";
import { VirtualAntennaDialog } from "./worstcaseData/VirtualAntennaDialog";

export const WorstcaseData = () => {

    const fileInputRef = useRef(null);
    const [from, setFrom] = useState(0);
    const [hasMore, setHasMore] = useState(true);
    const [searchQuery, setSearchQuery] = useState(localStorage.getItem("worstcaseDataSearch") || "");
    const [loading, setLoading] = useState(true);
    const [uploading, setUploading] = useState(false);
    const [showVirtualAntennaDialog, setShowVirtualAntennaDialog] = useState(false);
    const [isSynchronizing, setSynchronizing] = useState(false);

    const [antennas, setAntennas] = useState([]);

    useSse("UPDATE_ANTENNA", antenna => {
        const result = antennas.map(a => (a.id === antenna.id ? antenna : a));
        setAntennas(result);
    });

    useSse("INSERT_ANTENNA", antenna => {
        const result = [...antennas];
        result.unshift(antenna);
        setAntennas(result);
    });

    useSse("DELETE_ANTENNA", antennaId => {
        const result = antennas.filter(a => a.id !== antennaId);
        setAntennas(result);
    });

    const onLoadMore = async (antennas, from, pageSize, searchQuery) => {
        const recs = await antennaRest.getLazy(from, pageSize, searchQuery);
        if (recs === -1) {
            // something went wrong, for know we ignore this.
            return;
        }
        setFrom(recs.nextFrom ?? 0);
        setAntennas([...antennas, ...recs.list]);
        setHasMore(recs.hasMore);
        setLoading(false);
    };

    const onLoad = async (pageSize, searchQuery) => {
        setAntennas([]);
        setFrom(0);
        await onLoadMore([], 0, pageSize, searchQuery);
    };

    const onSearch = useDebounce((pageSize, searchQuery) => {
        setLoading(true);
        onLoad(pageSize, searchQuery);
    }, 500);

    const onSynchronize = async () => {
        setSynchronizing(true);
        await fieldRest.synchronize();
        onLoad(0, searchQuery);
        setSynchronizing(false);
    };

    const onUploadAntennas = async event => {
        const input = event.target;
        setUploading(true);

        try {
            const file = await commonUtils.readFile(event);
            const { result, name } = file;

            await antennaRest.importAntennas(name, result);

            setLoading(true);
            await onLoad(60, searchQuery);
        } finally {
            setUploading(false);
            input.value = "";
        }
    };


    useEffect(() => {
        Push.Permission.request();
        onSearch(60, searchQuery);
        // When many data are loaded and scroll is at the bottom, then the LazyScroll gets mad and loads
        // next page after next page, never stopping.
        // Reset scroll to the top fixes this issue.
        const content = document.getElementById("contentBody");
        if (content) {
            content.scrollTop = 0;
        }
    }, [searchQuery]);

    return (
        <>
            <Layout>
                <ContentHeader title={intl.get("navigation.worstcaseData")}>
                    <Button
                        svgIcon={mdiRefresh}
                        loading={isSynchronizing}
                        value={intl.get("worstcaseData.syncronize")}
                        onClick={onSynchronize} />
                    <Spacer variant="vertical" />
                    <Button
                        svgIcon={mdiPlus}
                        value={intl.get("worstcaseData.createVirtualAntenna")}
                        onClick={() => setShowVirtualAntennaDialog(true)} />
                    <Spacer variant="vertical" />
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept=".xlsx"
                        hidden
                        onChange={onUploadAntennas} />
                    <Button
                        svgIcon={mdiUpload}
                        value={intl.get("common.upload")}
                        disabled={uploading}
                        onClick={() => fileInputRef.current?.click()} />
                    <Spacer variant="vertical" />
                    <Search
                        id="worstcaseDataSearch"
                        value={searchQuery}
                        onChange={newVal => setSearchQuery(newVal)} />
                </ContentHeader>
                <ContentBody>
                    <LazyScroll
                        id="antennaTable"
                        hasMore={hasMore}
                        loadMore={() => onLoadMore(antennas, from, 60, searchQuery)}
                        loading={loading}
                        text={intl.get("common.loadingText")}>
                        <Table>
                            <thead>
                                <tr>
                                    <th width="50"></th>
                                    <th width="400">{intl.get("antennas.manufacturer")}</th>
                                    <th width="400">{intl.get("antennas.name")}</th>
                                    <th width="200">{intl.get("antennas.apertureHeight")}</th>
                                    <th width="100">{intl.get("antennas.originType")}</th>
                                    <th width="100">{intl.get("worstcaseData.resultAvailable")}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {antennas?.map(antenna =>
                                    <WorstcaseDataItem key={antenna.id} antenna={antenna} />
                                )}
                            </tbody>
                        </Table>
                    </LazyScroll>
                </ContentBody>
            </Layout>
            {showVirtualAntennaDialog &&
                <ValidationProvider>
                    <VirtualAntennaDialog
                        onClose={() => setShowVirtualAntennaDialog(false)} />
                </ValidationProvider>
            }
        </>
    );
};